"use strict";

import React, { useMemo, useState } from "react";
import {
  ListSubheader,
  MenuItem,
  Popover,
  TextField,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import classes from "./DialogueFilterBar.module.css";
import {
  DEFAULT_DIALOGUE_SEARCH_FILTERS,
  hasAnyDialogueSearchFilter,
} from "./DialogueSearchFilter";

// Lines whose take failed for the page's language in the latest create takes.
export const TAKE_FAILED_ATTENTION = "take-failed";

// Values match DIALOGUE_ATTENTION_FILTERS in the backend's utils/dialogueFilters.js.
export const TRANSLATION_ATTENTION_OPTIONS = {
  group: "Translation",
  options: [
    { value: "translation-longer", label: "Make It Longer" },
    { value: "translation-shorter", label: "Make It Shorter" },
    { value: "translation-overlap", label: "Overlapping" },
    { value: TAKE_FAILED_ATTENTION, label: "Failed to create take" },
  ],
};

// Lines the audio chopping service could not cut from the dialogue audio.
export const CHOPPING_FAILED_ATTENTION = "chopping-failed";

export const ORIGINAL_ATTENTION_OPTIONS = {
  group: "Original",
  options: [
    { value: "original-overlap", label: "Overlapping" },
    { value: CHOPPING_FAILED_ATTENTION, label: "Failed to chop" },
  ],
};

export const DIALOGUE_ATTENTION_OPTIONS = [
  TRANSLATION_ATTENTION_OPTIONS,
  ORIGINAL_ATTENTION_OPTIONS,
];

// Filter selects shown inline, or behind a "Filters" button that opens them
// in a popover (compact). Every change is applied immediately through onChange.
const DialogueFilterBar = ({
  value = DEFAULT_DIALOGUE_SEARCH_FILTERS,
  onChange,
  onClear,
  characters = [],
  getSpeakerDisplayName,
  emotionOptions = [],
  // [{ group: "Translation", options: [{ value, label }] }]
  attentionOptions = [],
  // Pages without translations (e.g. Transcriber) hide these.
  showGeneratedAudioFilter = true,
  showNoteFilter = false,
  // Performance Direction lines can be closed and reopened.
  showClosedFilter = false,
  // Performance Direction's "Use original" and "Subtitle only" row toggles.
  showRowFlagFilters = false,
  resultCount = null,
  // Total dialogues in the project, shown as "N of total dialogues".
  totalCount = 0,
  disabled = false,
  // Only the button is rendered in place; selects and status open in a popover.
  compact = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const filters = { ...DEFAULT_DIALOGUE_SEARCH_FILTERS, ...value };
  const isActive = hasAnyDialogueSearchFilter(filters);
  const activeFilterCount = [
    "speaker",
    "originalAudioEmotion",
    "hasTranslatedAudio",
    "hasNote",
    "closed",
    "lineType",
    "attention",
  ].filter((field) => filters[field]).length;

  const speakerOptions = useMemo(
    () =>
      characters
        .filter((character) => character?.speaker)
        .map((character) => ({
          value: character.speaker,
          label:
            typeof getSpeakerDisplayName === "function"
              ? getSpeakerDisplayName(character.speaker)
              : character.displayName || character.speaker,
        })),
    [characters, getSpeakerDisplayName]
  );

  // Keep the selected emotion listed even if the loaded page no longer has it.
  const normalizedEmotionOptions = useMemo(
    () =>
      [...new Set([...emotionOptions, filters.originalAudioEmotion])]
        .filter(Boolean)
        .sort((a, b) => String(a).localeCompare(String(b))),
    [emotionOptions, filters.originalAudioEmotion]
  );

  const updateFilter = (field, nextValue) => {
    onChange?.({ ...filters, [field]: nextValue });
  };

  const renderSelect = (field, label, allLabel, items) => (
    <TextField
      select
      size="small"
      label={label}
      value={filters[field]}
      onChange={(event) => updateFilter(field, event.target.value)}
      disabled={disabled}
      className={`${classes.FilterSelect} ${
        filters[field] ? classes.FilterSelectActive : ""
      }`}
      SelectProps={{ displayEmpty: true }}
      InputLabelProps={{ shrink: true }}
    >
      <MenuItem value="">
        <span className={classes.AllOption}>{allLabel}</span>
      </MenuItem>
      {items}
    </TextField>
  );

  const selects = (
    <>
      {renderSelect(
        "speaker",
        "Speaker",
        "All speakers",
        speakerOptions.map((speaker) => (
          <MenuItem key={speaker.value} value={speaker.value}>
            {speaker.label}
          </MenuItem>
        ))
      )}

      {renderSelect(
        "originalAudioEmotion",
        "Emotion",
        "All emotions",
        normalizedEmotionOptions.map((emotion) => (
          <MenuItem key={emotion} value={emotion}>
            {emotion}
          </MenuItem>
        ))
      )}

      {showGeneratedAudioFilter
        ? renderSelect("hasTranslatedAudio", "Generated audio", "Any", [
            <MenuItem key="true" value="true">
              Has generated audio
            </MenuItem>,
            <MenuItem key="false" value="false">
              No generated audio
            </MenuItem>,
          ])
        : null}

      {showNoteFilter
        ? renderSelect("hasNote", "Note", "Any", [
            <MenuItem key="true" value="true">
              Has note
            </MenuItem>,
            <MenuItem key="false" value="false">
              No note
            </MenuItem>,
          ])
        : null}

      {showClosedFilter
        ? renderSelect("closed", "Status", "Any", [
            <MenuItem key="false" value="false">
              Open
            </MenuItem>,
            <MenuItem key="true" value="true">
              Closed
            </MenuItem>,
          ])
        : null}

      {showRowFlagFilters
        ? renderSelect("lineType", "Line type", "Any", [
            <MenuItem key="useOriginal" value="useOriginal">
              Use original
            </MenuItem>,
            <MenuItem key="subtitleOnly" value="subtitleOnly">
              Subtitle only
            </MenuItem>,
            <MenuItem key="neither" value="neither">
              Neither
            </MenuItem>,
          ])
        : null}

      {attentionOptions.length > 0
        ? renderSelect(
            "attention",
            "Needs attention",
            "Any",
            attentionOptions.flatMap((group) => [
              <ListSubheader key={`group-${group.group}`}>
                {group.group}
              </ListSubheader>,
              ...group.options.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  className={classes.GroupedOption}
                >
                  {option.label}
                </MenuItem>
              )),
            ])
          )
        : null}
    </>
  );

  const status = isActive ? (
    <div className={classes.FilterStatus}>
      {resultCount !== null ? (
        <span className={classes.ResultCount}>
          {totalCount > 0
            ? `${resultCount} of ${totalCount} ${
                totalCount === 1 ? "dialogue" : "dialogues"
              }`
            : `${resultCount} ${resultCount === 1 ? "dialogue" : "dialogues"}`}
        </span>
      ) : null}

      <button
        type="button"
        className={classes.ClearButton}
        onClick={onClear}
        disabled={disabled}
      >
        Clear all
      </button>
    </div>
  ) : null;

  if (!compact) {
    return (
      <div className={classes.FilterBar}>
        {selects}
        {status}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`${classes.FilterButton} ${
          isActive ? classes.FilterButtonActive : ""
        }`}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
      >
        <FilterListIcon fontSize="small" />
        Filters
        {activeFilterCount > 0 ? (
          <span className={classes.FilterCount}>{activeFilterCount}</span>
        ) : null}
      </button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { className: classes.FilterPopover } }}
      >
        {selects}
        {status}
      </Popover>
    </>
  );
};

export default DialogueFilterBar;
